import type {
  AestheticsRecord,
  ActorProfile,
  AuthorFeedItem,
  FeedPost,
  InterestGroup,
  ProfileMusic,
  ProfilePicture,
  ProfileVideo,
} from './types';
import { shadertoyId } from '../lib/shadertoy';
import {
  customShaderSource,
  shaderPreset,
  type SkyspaceShader,
} from '../lib/shaders';

const SUPPORTED_FRAME_STYLES = new Set([
  'none',
  'hidden',
  'dotted',
  'dashed',
  'solid',
  'double',
  'groove',
  'ridge',
  'inset',
  'outset',
]);

const CSS_CURSOR_KEYWORDS = new Set([
  'auto',
  'default',
  'none',
  'context-menu',
  'help',
  'pointer',
  'progress',
  'wait',
  'cell',
  'crosshair',
  'text',
  'vertical-text',
  'alias',
  'copy',
  'move',
  'no-drop',
  'not-allowed',
  'grab',
  'grabbing',
  'all-scroll',
  'col-resize',
  'row-resize',
  'n-resize',
  'e-resize',
  's-resize',
  'w-resize',
  'ne-resize',
  'nw-resize',
  'se-resize',
  'sw-resize',
  'ew-resize',
  'ns-resize',
  'nesw-resize',
  'nwse-resize',
  'zoom-in',
  'zoom-out',
]);

export interface ClassicTheme {
  customized: boolean;
  backgroundUrl?: string;
  backgroundKind: 'color' | 'image' | 'video' | 'shader' | 'legacy-shadertoy';
  shader?: SkyspaceShader;
  backgroundPosition: string;
  backgroundColor: string;
  backgroundColorMode: 'solid' | 'pattern' | 'gradient';
  backgroundColorCycle: boolean;
  backgroundColorCycleSpeed: number;
  backgroundPattern: 'stripes' | 'checks';
  backgroundColors: string[];
  backgroundGradientAngle: number;
  bodyColor: string;
  titleColor: string;
  linkColor: string;
  font: string;
  panelColor: string;
  panelAlpha: number;
  frameColor: string;
  frameWidth: number;
  frameRadius: number;
  frameStyle: string;
  separatorColor: string;
  cursor?: string;
  cursorTrail?: string;
  cursorTrailParameters: string[];
}

export const defaultClassicTheme: ClassicTheme = {
  customized: false,
  backgroundKind: 'image',
  backgroundPosition: 'default',
  backgroundColor: '#ffffff',
  backgroundColorMode: 'solid',
  backgroundColorCycle: false,
  backgroundColorCycleSpeed: 0,
  backgroundPattern: 'stripes',
  backgroundColors: ['#ffffff', '#669acc'],
  backgroundGradientAngle: 135,
  bodyColor: '#000000',
  titleColor: '#ff5800',
  linkColor: '#000000',
  font: 'Arial',
  panelColor: '#ffffff',
  panelAlpha: 0.8,
  frameColor: '#ffffff',
  frameWidth: 0,
  frameRadius: 4,
  frameStyle: 'solid',
  separatorColor: '#ffcb99',
  cursorTrailParameters: [],
};

function safeColour(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  const trimmed = value.trim();
  return /^#[0-9a-f]{3,8}$/i.test(trimmed)
    || /^(?:rgb|hsl)a?\([\d\s.,%+-]+\)$/i.test(trimmed)
    || /^[a-z]+$/i.test(trimmed)
    ? trimmed
    : fallback;
}

function safeNumber(value: string | number | undefined, fallback: number, max: number): number {
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? Math.max(0, Math.min(max, parsed)) : fallback;
}

function safeHttpUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

const CURSOR_TRAILS = new Set([
  'none',
  'rainbow',
  'emoji',
  'elastic',
  'ghost',
  'trailing',
  'textflag',
  'dot',
  'bubbles',
  'snow',
  'fairy',
  'clock',
  'character',
]);

export function parseEffectParameters(value?: string): string[] {
  if (!value) return [];
  const parameters: string[] = [];
  for (const match of value.matchAll(/"([^"]{1,80})"|(\S{1,120})/g)) {
    parameters.push((match[1] || match[2]).slice(0, 120));
    if (parameters.length >= 12) break;
  }
  return parameters;
}

function legacyLine(text: string, label: string): string | undefined {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const value = text.match(new RegExp(`^#?${escaped}:?\\s*(.+)$`, 'im'))?.[1]?.trim();
  return value && value.toUpperCase() !== 'NULL' ? value : undefined;
}

export function parseLegacyAesthetics(feed: AuthorFeedItem[]): AestheticsRecord | undefined {
  const texts = feed.map(item => item.post.record?.text || '');
  const core = texts.find(text => {
    const lower = text.toLowerCase();
    return lower.includes('#skyspace') && lower.includes('#core');
  });
  const effectsPost = texts.find(text => {
    const lower = text.toLowerCase();
    return lower.includes('#skyspace') && lower.includes('#effects');
  });
  if (!core && !effectsPost) return undefined;

  const fontParts = legacyLine(core || '', 'font')?.split(/\s+/) || [];
  const colorStart = fontParts.findIndex(part => part.startsWith('#') || part === 'NULL');
  const frame = legacyLine(core || '', 'frame')?.split(/\s+/) || [];
  const trail = legacyLine(effectsPost || '', 'trail');
  const trailParts = parseEffectParameters(trail);
  const cursorValue = legacyLine(effectsPost || '', 'cursor');
  const customCursor = cursorValue && !CSS_CURSOR_KEYWORDS.has(cursorValue.toLowerCase())
    ? cursorValue
    : undefined;

  return {
    bg: {
      url: legacyLine(core || '', 'bg'),
      position: legacyLine(core || '', 'bgposition'),
    },
    panel: {
      frameThickness: frame[0]?.replace(/px$/i, ''),
      frameStyle: frame[1],
      frameColor: frame[2],
      panelColor: frame.length >= 6 ? frame[4] : frame[3],
      panelTransparency: frame.length >= 6 ? frame[5] : frame[4],
    },
    text: {
      font: colorStart > 0 ? fontParts.slice(0, colorStart).join(' ') : undefined,
      bodyColor: colorStart >= 0 ? fontParts[colorStart] : undefined,
      titleColor: colorStart >= 0 ? fontParts[colorStart + 1] : undefined,
      linkColor: colorStart >= 0 ? fontParts[colorStart + 2] : undefined,
    },
    bio: {
      quote: legacyLine(core || '', 'quote'),
      location: legacyLine(core || '', 'location'),
    },
    effects: {
      cursorImage: customCursor ? 'custom' : cursorValue,
      cursorURL: customCursor,
      cursorTrail: trailParts[0],
      cursorTrailParameters: trailParts.slice(1).map(value => (
        /\s/.test(value) ? `"${value}"` : value
      )).join(' '),
    },
  };
}

export function mergeProfileAesthetics(
  legacy?: AestheticsRecord,
  custom?: AestheticsRecord,
): AestheticsRecord | undefined {
  if (custom?.reset) return undefined;
  if (!custom) return legacy;
  return {
    ...legacy,
    ...custom,
    bg: { ...legacy?.bg, ...custom.bg },
    panel: { ...legacy?.panel, ...custom.panel },
    text: { ...legacy?.text, ...custom.text },
    bio: { ...legacy?.bio, ...custom.bio },
    effects: { ...legacy?.effects, ...custom.effects },
    layout: custom.layout || legacy?.layout,
  };
}

export function normalizeClassicTheme(record?: AestheticsRecord): ClassicTheme {
  const backgroundSource = record?.bg?.source;
  const backgroundUrl = backgroundSource === 'colour' || backgroundSource === 'shader'
    ? undefined
    : safeHttpUrl(
      backgroundSource === 'url'
        ? record?.bg?.url
        : record?.bg?.resolvedUrl || record?.bg?.url,
    );
  const backgroundColor = safeColour(record?.bg?.color, defaultClassicTheme.backgroundColor);
  const storedColorMode = record?.bg?.colorMode;
  const backgroundColorMode = storedColorMode === 'pattern' || storedColorMode === 'gradient'
    ? storedColorMode
    : defaultClassicTheme.backgroundColorMode;
  const backgroundColorCycle = record?.bg?.colorCycle === true
    || (record?.bg?.colorCycle === undefined && storedColorMode === 'cycle');
  const backgroundColorCycleSpeed = backgroundColorCycle
    ? safeNumber(record?.bg?.colorCycleSpeed, 60, 100)
    : 0;
  const backgroundColors = (record?.bg?.colors || [])
    .slice(0, 6)
    .map(value => safeColour(value, ''))
    .filter(Boolean);
  if (!backgroundColors.length) backgroundColors.push(backgroundColor);
  if (
    backgroundColors.length === 1
    && (backgroundColorMode !== 'solid' || backgroundColorCycle)
  ) {
    backgroundColors.push(defaultClassicTheme.backgroundColors[1]);
  }
  const frameStyle = record?.panel?.frameStyle?.toLowerCase();
  const cursorValue = record?.effects?.cursorImage?.toLowerCase();
  const rawTrail = record?.effects?.cursorTrail?.trim().toLowerCase();
  const customCursor = safeHttpUrl(record?.effects?.cursorURL);
  const cursor = cursorValue === 'custom'
    ? customCursor
    : cursorValue && CSS_CURSOR_KEYWORDS.has(cursorValue)
      ? cursorValue
      : undefined;

  const storedShader = record?.bg?.shader;
  const shader = backgroundSource === 'shader'
    ? {
      preset: storedShader?.preset === 'custom' || shaderPreset(storedShader?.preset)
        ? storedShader?.preset
        : 'pearlescent',
      code: storedShader?.preset === 'custom'
        ? storedShader.code?.slice(0, 50_000)
        : undefined,
      attribution: storedShader?.attribution?.trim().slice(0, 160),
      attributionUrl: safeHttpUrl(storedShader?.attributionUrl),
    }
    : undefined;
  const hasShader = shader?.preset === 'custom'
    ? Boolean(customShaderSource(shader))
    : Boolean(shaderPreset(shader?.preset));

  return {
    customized: Boolean(record),
    backgroundUrl,
    backgroundKind: backgroundSource === 'shader' && hasShader
      ? 'shader'
      : !backgroundUrl
      ? 'color'
      : backgroundUrl.match(/\.(?:mp4|webm|ogg)(?:\?|$)/i)
      ? 'video'
      : shadertoyId(backgroundUrl)
        ? 'legacy-shadertoy'
        : 'image',
    shader,
    backgroundPosition: record?.bg?.position?.trim().toLowerCase() || 'default',
    backgroundColor,
    backgroundColorMode,
    backgroundColorCycle,
    backgroundColorCycleSpeed,
    backgroundPattern: record?.bg?.pattern === 'checks' ? 'checks' : 'stripes',
    backgroundColors,
    backgroundGradientAngle: safeNumber(
      record?.bg?.gradientAngle,
      defaultClassicTheme.backgroundGradientAngle,
      360,
    ),
    bodyColor: safeColour(record?.text?.bodyColor, defaultClassicTheme.bodyColor),
    titleColor: safeColour(record?.text?.titleColor, defaultClassicTheme.titleColor),
    linkColor: safeColour(record?.text?.linkColor, defaultClassicTheme.linkColor),
    font: record?.text?.font?.trim().slice(0, 80) || defaultClassicTheme.font,
    panelColor: safeColour(record?.panel?.panelColor, defaultClassicTheme.panelColor),
    panelAlpha: safeNumber(record?.panel?.panelTransparency, defaultClassicTheme.panelAlpha, 1),
    frameColor: safeColour(record?.panel?.frameColor, defaultClassicTheme.frameColor),
    frameWidth: safeNumber(record?.panel?.frameThickness, defaultClassicTheme.frameWidth, 20),
    frameRadius: safeNumber(record?.panel?.frameRadius, defaultClassicTheme.frameRadius, 40),
    frameStyle: frameStyle && SUPPORTED_FRAME_STYLES.has(frameStyle)
      ? frameStyle
      : defaultClassicTheme.frameStyle,
    separatorColor: safeColour(
      record?.panel?.seperatorColor,
      defaultClassicTheme.separatorColor,
    ),
    cursor,
    cursorTrail: rawTrail && CURSOR_TRAILS.has(rawTrail) ? rawTrail : undefined,
    cursorTrailParameters: parseEffectParameters(record?.effects?.cursorTrailParameters),
  };
}

export function classicPanelColour(theme: ClassicTheme): string {
  const value = theme.panelColor;
  if (/^#[0-9a-f]{6}$/i.test(value)) {
    const red = Number.parseInt(value.slice(1, 3), 16);
    const green = Number.parseInt(value.slice(3, 5), 16);
    const blue = Number.parseInt(value.slice(5, 7), 16);
    return `rgba(${red}, ${green}, ${blue}, ${theme.panelAlpha})`;
  }
  return value;
}

export function parseInterests(feed: AuthorFeedItem[], customValue?: string): InterestGroup[] {
  const source = customValue !== undefined
    ? [customValue]
    : feed
      .map(item => item.post.record?.text || '')
      .filter(text => text.toLowerCase().includes('#skyspace')
        && text.toLowerCase().includes('#interests'));
  const groups = new Map<string, string[]>();

  source.forEach(text => {
    for (const match of text.matchAll(/\[([^\]\r\n]+)]\s*([^\r\n]*)/g)) {
      const category = match[1].trim();
      const terms = match[2]
        .replace(/#skyspace|#interests/gi, '')
        .split(',')
        .map(term => term.trim())
        .filter(Boolean);
      if (!category || terms.length === 0) continue;
      groups.set(category, [...new Set([...(groups.get(category) || []), ...terms])]);
    }
  });

  return [...groups].map(([category, terms]) => ({ category, terms }));
}

export function serializeInterests(groups: InterestGroup[]): string {
  return groups
    .map(group => ({
      category: group.category.trim().replace(/[\[\]\r\n]+/g, ''),
      terms: group.terms.map(term => term.trim()).filter(Boolean),
    }))
    .filter(group => group.category && group.terms.length > 0)
    .map(group => `[${group.category}] ${[...new Set(group.terms)].join(', ')}`)
    .join('\n');
}

function urlsFromText(value: string): string[] {
  return value.match(/https?:\/\/[^\s<>"']+/g) || [];
}

function youtubeMusicEmbed(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    let id: string | undefined;
    if (hostname === 'youtu.be') {
      id = parsed.pathname.split('/').filter(Boolean)[0];
    } else if (hostname === 'youtube.com' || hostname.endsWith('.youtube.com')) {
      id = parsed.searchParams.get('v')
        || parsed.pathname.match(/\/(?:embed|shorts|live)\/([^/]+)/)?.[1];
    }
    return id && /^[A-Za-z0-9_-]{6,20}$/.test(id)
      ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}`
      : undefined;
  } catch {
    return undefined;
  }
}

export function parseMusic(feed: AuthorFeedItem[], customValue?: string): ProfileMusic | undefined {
  const sources = customValue !== undefined
    ? [customValue]
    : feed
      .map(item => item.post.record?.text || '')
      .filter(text => text.toLowerCase().includes('#skyspace'));

  for (const source of sources) {
    for (const rawUrl of urlsFromText(source)) {
      const url = rawUrl.replace(/[),.;!?]+$/, '');
      const youtubeEmbed = youtubeMusicEmbed(url);
      if (youtubeEmbed) {
        return { provider: 'youtube', url, embedUrl: youtubeEmbed };
      }
      if (url.startsWith('https://music.apple.com/')) {
        return { provider: 'apple', url, embedUrl: url.replace('music.apple.com', 'embed.music.apple.com') };
      }
      if (url.startsWith('https://open.spotify.com/')) {
        const parsed = new URL(url);
        return {
          provider: 'spotify',
          url,
          embedUrl: `https://open.spotify.com/embed${parsed.pathname}`,
        };
      }
      if (url.startsWith('https://soundcloud.com/')) {
        return {
          provider: 'soundcloud',
          url,
          embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}`,
        };
      }
      if (/^https:\/\/[^/]+\.bandcamp\.com\//i.test(url)) {
        return { provider: 'bandcamp', url };
      }
      if (/\.midi?(?:\?|$)/i.test(url)) {
        return { provider: 'midi', url };
      }
    }
  }
  return undefined;
}

function cleanCaption(value: string): string {
  return value.replace(/https?:\/\/\S+/gi, '').replace(/[\r\n]+/g, ' ').trim();
}

export function extractPictures(feed: AuthorFeedItem[]): ProfilePicture[] {
  return feed.flatMap(item => {
    if (item.reason) return [];
    const post = item.post;
    const images = post.embed?.images || [];
    return images.map(image => ({
      url: image.fullsize || image.thumb || '',
      thumbnail: image.thumb || image.fullsize || '',
      alt: image.alt || '',
      caption: cleanCaption(post.record.text || ''),
      postUri: post.uri,
    })).filter(image => Boolean(image.url));
  });
}

export function extractVideos(feed: AuthorFeedItem[]): ProfileVideo[] {
  return feed.flatMap(item => {
    if (item.reason) return [];
    const post = item.post;
    const caption = cleanCaption(post.record.text || '');
    if (post.embed?.playlist) {
      return [{
        url: post.embed.playlist,
        thumbnail: post.embed.thumbnail,
        caption,
        postUri: post.uri,
      }];
    }
    const external = post.embed?.external;
    if (external?.uri && /(?:youtube\.com|youtu\.be|vimeo\.com|\.(?:mp4|webm|ogg)(?:\?|$))/i.test(external.uri)) {
      return [{
        url: external.uri,
        thumbnail: external.thumb,
        caption,
        postUri: post.uri,
      }];
    }
    return urlsFromText(post.record.text || '')
      .filter(url => /(?:youtube\.com|youtu\.be|vimeo\.com|\.(?:mp4|webm|ogg)(?:\?|$))/i.test(url))
      .map(url => ({ url, caption, postUri: post.uri }));
  });
}

export function legacyWhoToMeet(feed: AuthorFeedItem[]): string | undefined {
  for (const item of feed) {
    const text = item.post.record?.text || '';
    if (!text.toLowerCase().includes('#skyspace')) continue;
    const match = text.match(/\bi['’]?(?:d)?\s+like\s+to\s+meet\b([\s\S]+)/i);
    if (match?.[1]?.trim()) {
      const value = match[1].replace(/#skyspace/gi, '').trim();
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  }
  return undefined;
}

export function firstRegularPost(feed: AuthorFeedItem[]) {
  return regularPosts(feed)[0];
}

export function regularPosts(feed: AuthorFeedItem[]): FeedPost[] {
  return feed.filter(item => {
    const text = item.post.record?.text || '';
    return !item.reason
      && !item.post.record?.reply
      && Boolean(text)
      && !text.toLowerCase().includes('#skyspace');
  }).map(item => item.post);
}

function top8Source(feed: AuthorFeedItem[], customValue?: string): string {
  const legacyValue = feed
    .map(item => item.post.record?.text || '')
    .find(text => {
      const normalized = text.toLowerCase();
      return normalized.includes('#skyspace')
        && (normalized.includes('top8') || normalized.includes('#top8'));
    });
  return customValue !== undefined ? customValue : legacyValue || '';
}

export function parseTop8Slots(
  feed: AuthorFeedItem[],
  customValue?: string,
): Array<string | undefined> {
  const source = top8Source(feed, customValue);
  const tokens = source.split(/\s+/).filter(Boolean);
  if (tokens.includes('-')) {
    const seen = new Set<string>();
    return Array.from({ length: 8 }, (_, index) => {
      const match = tokens[index]?.match(/^@([A-Za-z0-9._-]+)$/);
      const handle = match?.[1].toLowerCase();
      if (!handle || seen.has(handle)) return undefined;
      seen.add(handle);
      return handle;
    });
  }
  const handles = [...source.matchAll(/@([A-Za-z0-9._-]+)/g)]
    .map(match => match[1].toLowerCase());
  const unique = [...new Set(handles)].slice(0, 8);
  return Array.from({ length: 8 }, (_, index) => unique[index]);
}

export function parseTop8Handles(feed: AuthorFeedItem[], customValue?: string): string[] {
  return parseTop8Slots(feed, customValue).filter(
    (handle): handle is string => Boolean(handle),
  );
}

export function serializeTop8Slots(slots: Array<string | undefined>): string {
  return Array.from({ length: 8 }, (_, index) => {
    const handle = slots[index]?.trim().replace(/^@/, '').toLowerCase();
    return handle ? `@${handle}` : '-';
  }).join('\n');
}

export function selectTopFriends(
  forcedHandles: string[],
  profiles: ActorProfile[],
  limit = 8,
): ActorProfile[] {
  const byHandle = new Map(profiles.map(profile => [profile.handle.toLowerCase(), profile]));
  const selected: ActorProfile[] = [];
  const seen = new Set<string>();

  for (const handle of forcedHandles) {
    const normalized = handle.toLowerCase();
    if (seen.has(normalized)) continue;
    const profile = byHandle.get(normalized);
    if (profile) {
      selected.push(profile);
      seen.add(normalized);
    }
  }
  for (const profile of profiles) {
    const normalized = profile.handle.toLowerCase();
    if (!seen.has(normalized)) {
      selected.push(profile);
      seen.add(normalized);
    }
    if (selected.length >= limit) break;
  }
  return selected.slice(0, limit);
}

export function selectTopFriendSlots(
  slots: Array<string | undefined>,
  profiles: ActorProfile[],
  limit = 8,
): ActorProfile[] {
  const byHandle = new Map(profiles.map(profile => [profile.handle.toLowerCase(), profile]));
  const reserved = new Set(
    slots
      .map(handle => handle?.toLowerCase())
      .filter((handle): handle is string => Boolean(handle && byHandle.has(handle))),
  );
  const fallback = profiles.filter(profile => !reserved.has(profile.handle.toLowerCase()));
  const used = new Set<string>();
  let fallbackIndex = 0;

  const selected = Array.from({ length: limit }, (_, index) => {
    const pinnedHandle = slots[index]?.toLowerCase();
    const pinned = pinnedHandle ? byHandle.get(pinnedHandle) : undefined;
    if (pinned && !used.has(pinned.handle.toLowerCase())) {
      used.add(pinned.handle.toLowerCase());
      return pinned;
    }
    while (fallbackIndex < fallback.length) {
      const candidate = fallback[fallbackIndex++];
      const normalized = candidate.handle.toLowerCase();
      if (!used.has(normalized)) {
        used.add(normalized);
        return candidate;
      }
    }
    return undefined;
  });
  return selected.filter((profile): profile is ActorProfile => Boolean(profile));
}

export function friendComments(posts: FeedPost[], handle: string): FeedPost[] {
  const escaped = handle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const prefix = new RegExp(`^#skyspace\\s+@${escaped}(?=\\s|$)`, 'i');
  return posts.filter(post => prefix.test(post.record?.text || ''));
}

export function commentText(text: string, handle?: string): string {
  if (!handle) return text;
  const escaped = handle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(new RegExp(`^#skyspace\\s+@${escaped}(?=\\s|$)\\s*`, 'i'), '').trim();
}
